import React from 'react';
import {
    Box,
    CircularProgress,
    Tab,
    Tabs,
    Tooltip,
    Typography,
    alpha,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import useTicketList from './use-ticket-list';
import TicketInfoChip from '../ticket-info-chip';
import { timeConverter } from 'utils/utils';

const MenuItemTab = ({ item }: any) => {
    const { id, title, status, created_at, last_message } = item;

    return (
        <Box
            sx={{
                width: '100%',
                minWidth: 0,
                p: 1.75,
                textAlign: 'right',
            }}>
            <Box
                sx={{
                    width: '100%',
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1.25,
                }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        className="ticket-title"
                        sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: 'text.primary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                        {title}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', mt: 0.25, display: 'block' }}>
                        شناسه تیکت {id}
                    </Typography>
                </Box>

                <Box sx={{ flexShrink: 0, pt: 0.15 }}>
                    <TicketInfoChip value={status} />
                </Box>
            </Box>

            <Typography
                variant="body2"
                sx={{
                    color: 'text.secondary',
                    mt: 1.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minHeight: 21,
                }}>
                {last_message?.text || 'هنوز پیامی برای این تیکت ثبت نشده است.'}
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    mt: 1,
                }}>
                <Tooltip title={timeConverter(created_at)} placement="bottom" arrow>
                    <Typography
                        variant="caption"
                        sx={{ color: 'text.disabled', fontSize: 11.5 }}>
                        {timeConverter(created_at, 'date')}
                    </Typography>
                </Tooltip>
            </Box>
        </Box>
    );
};

const TicketMenuItems = (props: any) => {
    const theme = useTheme();
    const isVerticalOrientation = useMediaQuery(theme.breakpoints.up('md'));

    const {
        panelIndex,
        ticketsList,
        onChangeMenu,
        onLoadMoreTickets,
        currentPage,
        totalPages,
        isLoadingNewTickets,
        scrollPosition,
        containerRef,
        onScroll,
    } = useTicketList(props);

    const thereAreTickets = ticketsList && ticketsList.length > 0;

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                overflow: 'hidden',
            }}>
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                    تیکت‌های من
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    آخرین درخواست‌ها و پاسخ‌ها
                </Typography>
            </Box>

            {thereAreTickets ? (
                <Box
                    ref={containerRef}
                    onScroll={onScroll}
                    sx={{
                        width: '100%',
                        height: '100%',
                        overflowY: 'auto',
                        scrollBehavior: !scrollPosition ? 'smooth' : 'auto',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        },
                    }}>
                    <Tabs
                        value={panelIndex}
                        onChange={onChangeMenu}
                        orientation="vertical"
                        variant="scrollable"
                        allowScrollButtonsMobile
                        TabIndicatorProps={{ style: { display: 'none' } }}
                        sx={{
                            width: '100%',
                            direction: 'rtl',
                            '& .MuiTabs-indicator': { display: 'none !important' },
                            '& .MuiTabs-scroller': {
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                            },
                            '& .MuiTabs-scroller::-webkit-scrollbar': {
                                display: 'none',
                            },
                        }}>
                        {ticketsList.map((item: any) => (
                            <Tab
                                key={item?.id}
                                label={<MenuItemTab item={item} />}
                                sx={{
                                    minWidth: '100%',
                                    maxWidth: '100%',
                                    minHeight: 0,
                                    p: 0,
                                    alignItems: 'stretch',
                                    boxSizing: 'border-box',
                                    borderBottom: '1px solid',
                                    borderBottomColor: 'divider',
                                    // Logical border is intentional: Stylis RTL flips physical
                                    // left/right properties. inline-start stays on the visual right in RTL.
                                    borderInlineStart: '4px solid transparent',
                                    borderInlineEnd: '0 !important',
                                    color: 'text.primary',
                                    position: 'relative',
                                    transition: 'background-color .15s ease, border-color .15s ease',
                                    '&:hover': {
                                        bgcolor: 'action.hover',
                                    },
                                    '&.Mui-selected': {
                                        color: 'text.primary',
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        borderInlineStartColor: `${theme.palette.primary.main} !important`,
                                        borderInlineEnd: '0 !important',
                                    },
                                }}
                            />
                        ))}

                        {!isVerticalOrientation && currentPage < totalPages && (
                            <Tab
                                label="بیشتر..."
                                onClick={onLoadMoreTickets}
                                sx={{
                                    color: 'primary.main',
                                    minHeight: 48,
                                }}
                            />
                        )}
                    </Tabs>

                    {isLoadingNewTickets && (
                        <Box
                            sx={{
                                width: '100%',
                                display: { xs: 'none', md: 'flex' },
                                justifyContent: 'center',
                                py: 2,
                            }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        px: 2,
                        textAlign: 'center',
                    }}>
                    <img
                        src="/images/no-exist.png"
                        style={{ width: '72px', marginBottom: '12px' }}
                        alt="no data"
                    />
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                        تیکتی برای نمایش وجود ندارد
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', mt: 0.5 }}>
                        برای شروع می‌توانید یک تیکت جدید ایجاد کنید.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default TicketMenuItems;
